/**
 * phoneNormalize.js  — TREATX-UAT-RELEASE-FIX-SEQUENCE-01 / Phase 2
 *
 * Lightweight E.164 normalization helpers. Built without an external
 * dependency to honour the task constraint "do not introduce new
 * architecture / libraries". Covers the 17 markets exposed in the
 * canonical COUNTRIES list (ClinicApplicationContext.COUNTRIES) plus
 * the most common origins of inbound patient leads.
 *
 * E.164 rules enforced (per ITU-T E.164):
 *   • Leading "+"
 *   • Total digits after "+" = 8..15
 *   • No characters other than digits
 *
 * Designed to be paired with the reusable <PhoneInput /> component
 * (see /app/frontend/src/components/ui/phone-input.jsx). Callers should
 * store the canonical E.164 string returned by `toE164` — never the
 * user-typed value.
 *
 * Not a phone library — when stronger validation is needed (network
 * lookups, fixed-line vs mobile, region-specific length tables, etc.)
 * swap to `libphonenumber-js` and re-target these helpers. No callers
 * will need to change.
 */

// ── Country dial-code table ───────────────────────────────────────────────
// Limited intentionally to markets currently served + common originators.
// Add entries when a real owner test surfaces a new origin country.
//
// `iso` is ISO-3166 alpha-2; `dial` is the E.164 country calling code
// without leading "+"; `minNational` / `maxNational` are inclusive bounds
// on the digit count of the subscriber portion (post-dial). These are
// best-effort and intentionally permissive — they reject typos, not
// regional sub-formats.
export const DIAL_CODES = [
  { iso: 'TR', name: 'Turkey',               dial: '90',  minNational: 10, maxNational: 10 },
  { iso: 'TH', name: 'Thailand',             dial: '66',  minNational: 8,  maxNational: 9  },
  { iso: 'MX', name: 'Mexico',               dial: '52',  minNational: 10, maxNational: 10 },
  { iso: 'IN', name: 'India',                dial: '91',  minNational: 10, maxNational: 10 },
  { iso: 'MY', name: 'Malaysia',             dial: '60',  minNational: 9,  maxNational: 10 },
  { iso: 'SG', name: 'Singapore',            dial: '65',  minNational: 8,  maxNational: 8  },
  { iso: 'AE', name: 'United Arab Emirates', dial: '971', minNational: 8,  maxNational: 9  },
  { iso: 'DE', name: 'Germany',              dial: '49',  minNational: 6,  maxNational: 13 },
  { iso: 'ES', name: 'Spain',                dial: '34',  minNational: 9,  maxNational: 9  },
  { iso: 'KR', name: 'South Korea',          dial: '82',  minNational: 9,  maxNational: 10 },
  { iso: 'BR', name: 'Brazil',               dial: '55',  minNational: 10, maxNational: 11 },
  { iso: 'CO', name: 'Colombia',             dial: '57',  minNational: 10, maxNational: 10 },
  { iso: 'PL', name: 'Poland',               dial: '48',  minNational: 9,  maxNational: 9  },
  { iso: 'HU', name: 'Hungary',              dial: '36',  minNational: 8,  maxNational: 9  },
  { iso: 'CZ', name: 'Czech Republic',       dial: '420', minNational: 9,  maxNational: 9  },
  { iso: 'GB', name: 'United Kingdom',       dial: '44',  minNational: 10, maxNational: 10 },
  // Common inbound origins not in the served-market catalog
  { iso: 'US', name: 'United States',        dial: '1',   minNational: 10, maxNational: 10 },
  { iso: 'CA', name: 'Canada',               dial: '1',   minNational: 10, maxNational: 10 },
  { iso: 'FR', name: 'France',               dial: '33',  minNational: 9,  maxNational: 9  },
  { iso: 'IT', name: 'Italy',                dial: '39',  minNational: 9,  maxNational: 11 },
  { iso: 'NL', name: 'Netherlands',          dial: '31',  minNational: 9,  maxNational: 9  },
  { iso: 'AU', name: 'Australia',            dial: '61',  minNational: 9,  maxNational: 9  },
  { iso: 'SA', name: 'Saudi Arabia',         dial: '966', minNational: 9,  maxNational: 9  },
  { iso: 'QA', name: 'Qatar',                dial: '974', minNational: 8,  maxNational: 8  },
  { iso: 'EG', name: 'Egypt',                dial: '20',  minNational: 9,  maxNational: 10 },
  { iso: 'GR', name: 'Greece',               dial: '30',  minNational: 10, maxNational: 10 },
];

const DIAL_BY_ISO  = DIAL_CODES.reduce((m, c) => { m[c.iso] = c; return m; }, {});
// Build a length-descending lookup of dial → entry so that "1" doesn't
// match before "971" / "972" etc. when parsing inbound strings.
const DIAL_PREFIXES = DIAL_CODES
  .map((c) => c.dial)
  .filter((v, i, a) => a.indexOf(v) === i)
  .sort((a, b) => b.length - a.length);
const ENTRIES_BY_DIAL = DIAL_CODES.reduce((m, c) => {
  if (!m[c.dial]) m[c.dial] = [];
  m[c.dial].push(c);
  return m;
}, {});

export function countryByIso(iso) {
  return DIAL_BY_ISO[String(iso || '').toUpperCase()] || null;
}

// ── Input normalization while typing ──────────────────────────────────────
/**
 * Sanitise raw input for an E.164 phone input. Allows a single leading
 * "+" followed by digits only; everything else is dropped. Hard caps at
 * 16 chars (the maximum valid E.164 with leading "+" = 16).
 */
export function normalizePhoneInput(raw) {
  if (raw === null || raw === undefined) return '';
  const str = String(raw);
  // Keep leading '+' only when it is the first character
  let cleaned = str.replace(/[^\d+]/g, '');
  cleaned = cleaned.replace(/(?!^)\+/g, '');
  return cleaned.slice(0, 16);
}

/**
 * Same as above but for the national portion only (no '+' allowed).
 * Used by the dropdown-flavoured PhoneInput where the dial code is
 * managed separately from the digits.
 */
export function normalizeNationalInput(raw) {
  if (raw === null || raw === undefined) return '';
  return String(raw).replace(/\D+/g, '').slice(0, 15);
}

// ── Compose / decompose ───────────────────────────────────────────────────
/**
 * Compose an E.164 string from a country iso + national digits.
 * Returns '' when either side is empty so the caller can store empty
 * values verbatim. Trims a leading 0 from the national portion (common
 * trunk prefix in many countries).
 */
export function composeE164(iso, national) {
  const c = countryByIso(iso);
  if (!c) return '';
  const digits = normalizeNationalInput(national).replace(/^0+/, '');
  if (!digits) return '';
  return `+${c.dial}${digits}`;
}

/**
 * Parse an existing string into { iso, national }. Best-effort: matches
 * the longest known dial prefix. Falls back to null on unparseable
 * input. Used to hydrate the PhoneInput when an existing record is
 * loaded.
 */
export function parseE164(value) {
  if (!value) return null;
  const cleaned = normalizePhoneInput(value);
  if (!cleaned.startsWith('+')) return null;
  const digits = cleaned.slice(1);
  for (const dial of DIAL_PREFIXES) {
    if (digits.startsWith(dial)) {
      const candidates = ENTRIES_BY_DIAL[dial] || [];
      // Pick the first candidate; ambiguity (e.g. +1 US vs CA) is OK —
      // both dial-code 1 entries are functionally equivalent for E.164.
      const iso = candidates[0]?.iso || null;
      return { iso, national: digits.slice(dial.length) };
    }
  }
  return null;
}

// ── Validation ────────────────────────────────────────────────────────────
/**
 * Validate a string against ITU-T E.164 + (best-effort) per-country
 * national length bounds. Empty input is considered VALID; require it
 * separately at the form layer.
 *
 * @returns {{ valid: boolean, error?: string, iso?: string }}
 */
export function validateE164(value) {
  if (value === '' || value === null || value === undefined) {
    return { valid: true };
  }
  const cleaned = normalizePhoneInput(value);
  if (!cleaned.startsWith('+')) {
    return { valid: false, error: 'Phone must start with country code (e.g. +90)' };
  }
  const digits = cleaned.slice(1);
  if (!/^\d{8,15}$/.test(digits)) {
    return { valid: false, error: 'Phone must have 8–15 digits after the country code' };
  }
  const parsed = parseE164(cleaned);
  if (parsed && parsed.iso) {
    const meta = DIAL_BY_ISO[parsed.iso];
    if (meta) {
      const n = parsed.national.length;
      if (n < meta.minNational || n > meta.maxNational) {
        return {
          valid: false,
          error: `${meta.name}: national number must be ${
            meta.minNational === meta.maxNational
              ? `${meta.minNational}`
              : `${meta.minNational}–${meta.maxNational}`
          } digits`,
          iso: parsed.iso,
        };
      }
      return { valid: true, iso: parsed.iso };
    }
  }
  // Country code unknown to our table — accept length-only and surface a
  // soft notice for analytics. Better than rejecting a legitimate number
  // from a market we have not added yet.
  return { valid: true, iso: null };
}

// ── Display formatter (very light) ────────────────────────────────────────
/**
 * Format an E.164 number for human display: "+90 555 123 45 67".
 * Groups of 3 (then 2 then 2) for readability — purely cosmetic, not a
 * region-specific format. Inputs that fail validation are returned as-is.
 */
export function formatE164ForDisplay(value) {
  if (!value) return '';
  const v = normalizePhoneInput(value);
  if (!v.startsWith('+')) return v;
  const parsed = parseE164(v);
  if (!parsed) return v;
  const meta = DIAL_BY_ISO[parsed.iso || ''];
  const dial = meta?.dial || '';
  const tail = parsed.national;
  // Simple 3-3-2-2 grouping for the national portion
  const groups = [];
  let remaining = tail;
  const sizes = [3, 3, 2, 2, 2];
  for (const sz of sizes) {
    if (!remaining) break;
    groups.push(remaining.slice(0, sz));
    remaining = remaining.slice(sz);
  }
  if (remaining) groups.push(remaining);
  return `+${dial}${groups.length ? ' ' + groups.join(' ') : ''}`;
}
